/*
 * About this helper:
 * A point is an outlier if
 * 1. it's not null
 * 2. it's within 2 sds from the mean
 * 3. Because we're only looking at dips, we will call outlier any point that are below the mean - 2 standard deviations.
 */

/**
 * A value is an outlier if it is a number of standard deviations below the mean
 * @param {number} value            The suspected outlier value (number)
 * @param {number} mean             The mean of the dataset values
 * @param {number} sd               The standard deviation of the dataset values
 * @param {number} deviationWeight  Number of standard deviations away from the mean that defines a point as an outlier. Default to 2.
 */
const isOutlier = ({ value, mean, sd, deviationWeight }: any): any =>
  value <= mean - deviationWeight * sd;

/**
 * Compute the sum of the values of an array
 * @param {array} values  The dataset values (numbers)
 */
const getSum = (values: any): any =>
  values.reduce((acc: any, value: any) => acc + value, 0);

/**
 * Compute the sum of the squared values of an array
 * @param {array} values  The dataset values (numbers)
 */
const getSquaredSum = (values: any): any =>
  values.reduce((acc: any, value: any) => acc + value * value, 0);

/**
 * Compute the mean of the values of an array
 * @param {array} values  The dataset values (numbers)
 */
const getMean = (values: any): any => getSum(values) / values.length;

/**
 * Compute the variance of the values of an array
 * @param {array} values  The dataset values (numbers)
 * @param {number} mean   The mean of the dataset values
 */
const getVariance = (values: any, mean: any): any =>
  getSquaredSum(values) / values.length - mean * mean;

/**
 * Compute the standard deviation of the values of an array
 * @param {array} values  The dataset values (numbers)
 * @param {number} mean   The mean of the dataset values
 */
const getStandardDeviation = (values: any, mean: any): any =>
  Math.sqrt(getVariance(values, mean));

/**
 * Set the value of outliers points to null in a trend data array
 * @param {array} points  An array of objects containing values [ {keyToCheck: ...},  {keyToCheck: ...}, ...]
 * @param {string} keyToCheck      The name of the property to look for values
 * @param {number} mean            The mean of the dataset values
 * @param {number} sd              The standard deviation of the dataset values
 * @param {number} deviationWeight Number of standard deviations away from the mean that defines a point as an outlier. Default to 2.
 */
const setOutliers = ({
  points,
  keyToCheck,
  mean,
  sd,
  deviationWeight,
}: any): any => {
  let foundValidPoint = false;

  const cleanedPoints = [];
  // Walk through the items in a reverse order so from date new to old.
  for (let index = points.length - 1; index >= 0; index--) {
    const point = points[index];
    const pointIsOutlier = isOutlier({
      value: point[keyToCheck],
      mean,
      sd,
      deviationWeight,
    });

    if (!foundValidPoint && pointIsOutlier) {
      cleanedPoints.unshift({
        ...point,
        [keyToCheck]: null,
        percentage: null, // for Source of Streams
      });
    } else {
      // If a valid point is found, make sure that all older data points are also valid.
      foundValidPoint = true;
      cleanedPoints.unshift(point);
    }
  }
  return cleanedPoints;
};
/**
 * Get the non-null values of a value data array
 * @param {array} points      An array of values
 */
const getValuesWithoutNull = (points: any): any =>
  points.filter((d: any) => d !== null);

/**
 * Get the values of a trend data array
 * @param {array} points      An array of objects containing values [ {keyToCheck: ...},  {keyToCheck: ...}, ...]
 * @param {string} keyToCheck   The name of the property to look for values
 * @param {number} amountOfItemsToCompareAgainst   The number of points in the subset we will use to compare values
 */
const getValues = (
  points: any,
  keyToCheck: any,
  amountOfItemsToCompareAgainst: any
): any => {
  const values = getValuesWithoutNull(points.map((d: any) => d[keyToCheck]));

  return values.slice(values.length - amountOfItemsToCompareAgainst);
};

/**
 * Given a trend data array, look at n last data points and remove suspicious outliers.
 * Default deviationWeight is 2. If you want the algorithm to be more aggressive, decrease this number.
 * @param {array} args.source                  An array of objects containing values [ {keyToCheck: ...},  {keyToCheck: ...}, ...]
 * @param {string} args.keyToCheck             The name of the property to look for values
 * @param {number} args.amountOfItemsToCheck   Number of suspected data points at the end of array. n should be smaller than the length of the data array.
 * @param {number} args.deviationWeight        Number of standard deviations away from the mean that defines a point as an outlier. Default to 2.
 * @param {number} args.amountOfItemsToCompareAgainst   The number of points in the subset we will use to compare values
 */
const removeTrendOutliers = (args: any): any => {
  const {
    source,
    keyToCheck,
    amountOfItemsToCheck,
    amountOfItemsToCompareAgainst = 3,
    deviationWeight = 2,
    isChartDipsOn = true,
  } = args;

  if (!isChartDipsOn) return source;

  if (!source || !keyToCheck || !amountOfItemsToCheck) {
    const received = JSON.stringify(args);
    const msg = `Expected valid: {source, keyToCheck, amountOfItemsToCheck} but got ${received}`;
    throw new Error(msg);
  }

  // Extract the the last N (amountOfItemsToCheck) points from the array
  const headPoints = source.slice(0, source.length - amountOfItemsToCheck);
  const tailPoints = source.slice(source.length - amountOfItemsToCheck);

  // Get the last 3 values of headPoints, excluding any nulls that are correct for sure.
  const values = getValues(
    headPoints,
    keyToCheck,
    amountOfItemsToCompareAgainst
  );

  // Compute the mean and standard deviation
  const mean = getMean(values);
  const sd = getStandardDeviation(values, mean);

  // If mean or sd is undefined, return original array
  if (!(mean && sd)) {
    return source;
  }

  // WMG came up with this mean threshold of 25
  // to make sure low-volumed points don't get trimmed.
  if (mean <= 25) {
    return source;
  }

  // Set outlier values to null
  const tailPointsCleaned = setOutliers({
    points: tailPoints,
    keyToCheck,
    mean,
    sd,
    deviationWeight,
  });

  // Return cleaned dataset!
  return headPoints.concat(tailPointsCleaned);
};

export default removeTrendOutliers;
